import type { CSSProperties, FC } from "react";
import { useEffect, useState } from "react";

import { useBlocNext } from "@blac/react";
import { useMediaQuery } from "@react-hooks-library/core";
import {
  AddMealInsightFeedbackRequest,
  MealInsightHistoryResponse
} from "@9amhealth/openapi";
import { Modal } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { ZonedDateTime } from "@internationalized/date";
import { APP_BREAKPOINT } from "src/constants/layout";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit from "src/state/MealsStatsCubit/MealsStatsCubit";
import { fileState } from "src/state/state";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import Translate from "src/ui/components/Translate/Translate";

import Feedback from "./Feedback";
import MealInsightActionButton from "./MealInsightActionButton";
import MealInsightContent from "./MealInsightContent";
import MealRefineBar from "./MealRefineBar";
import MealRefineDialog from "./MealRefineDialog";
import MealTakenAtEditor from "./MealTakenAtEditor";
import {
  mealTakenAtAsZoned,
  toOffsetDateTimeString
} from "./mealInsightHelpers";

const PREVIEW_MAX_WIDTH = 500;

const FEEDBACK_TO_RATING: Record<
  MealInsightHistoryResponse.latestFeedback,
  AddMealInsightFeedbackRequest.rating
> = {
  [MealInsightHistoryResponse.latestFeedback.GOOD]:
    AddMealInsightFeedbackRequest.rating.GOOD,
  [MealInsightHistoryResponse.latestFeedback.BAD]:
    AddMealInsightFeedbackRequest.rating.BAD,
  [MealInsightHistoryResponse.latestFeedback.REMOVED]:
    AddMealInsightFeedbackRequest.rating.REMOVED
};

const DESKTOP_MODAL_INSET = "2rem";

/**
 * The modal's scroll container reserves the bottom safe area, which lifts the
 * sticky action bar off the bottom of the screen. The bar reserves that space
 * itself, so the scroller must not reserve it twice.
 */
const MealModal = styled(Modal)`
  && > * {
    padding-bottom: 0;
  }
`;

const MODAL_STYLE = {
  "--dialog-background": "var(--color-cream, #fffcf3)",
  "--width": "100vw",
  "--max-width": `calc(100vw - ${DESKTOP_MODAL_INSET})`,
  "--height": "100dvh",
  "--max-height": `calc(100dvh - ${DESKTOP_MODAL_INSET})`,
  "--border-radius": "1rem"
} as CSSProperties;

interface MealInsightDialogProps {
  meal: MealInsightHistoryResponse;
  onClose: () => void;
}

const MealInsightDialog: FC<MealInsightDialogProps> = ({ meal, onClose }) => {
  const [{ meals }, { deleteMeal, rateMeal, refineMeal, updateMealTakenAt }] =
    useBlocNext(MealInsightsCubit);
  const [, { invalidate: invalidateMealStats }] = useBlocNext(MealsStatsCubit);
  const [isOpen, setIsOpen] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [takenAtDialogOpen, setTakenAtDialogOpen] = useState(false);
  const [refining, setRefining] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${APP_BREAKPOINT}px)`);

  // The `meal` prop is the snapshot the list handed over on click, so an edited
  // date would otherwise keep reading the old value until the dialog is
  // reopened. Fall back to that snapshot once a delete drops the meal from the
  // cubit, so the header stays intact through the dismiss animation.
  const current = meals.find((entry) => entry.id === meal.id) ?? meal;
  const { takenAt } = current;

  useEffect(() => {
    let active = true;
    const windowWidth = window.innerWidth;
    const density = window.devicePixelRatio;
    const width = Math.round(
      Math.min(windowWidth, PREVIEW_MAX_WIDTH) * density
    );
    void fileState.loadPreview(meal.fileId, width, width).then((url) => {
      if (active && url) {
        setImageUrl(url);
      }
    });
    return () => {
      active = false;
    };
  }, [meal.fileId]);

  useEffect(() => {
    if (!imageUrl) {
      return;
    }
    setImageLoaded(false);
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = imageUrl;
  }, [imageUrl]);

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const handleTakenAtSaved = (picked: ZonedDateTime): void => {
    if (picked.compare(mealTakenAtAsZoned(takenAt)) === 0) {
      return;
    }
    void updateMealTakenAt(meal.id, toOffsetDateTimeString(picked)).then(
      (updated) => {
        if (updated) {
          invalidateMealStats();
        }
      }
    );
  };

  const handleRefine = async (correction: string): Promise<boolean> => {
    const refined = await refineMeal(meal.id, correction);
    if (refined) {
      setRefining(false);
      invalidateMealStats();
    }
    return refined;
  };

  const handleConfirmDelete = async (): Promise<void> => {
    const deleted = await deleteMeal(meal.id);
    if (deleted) {
      invalidateMealStats();
      handleClose();
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }
      if (refining) {
        setRefining(false);
        return;
      }
      handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [refining]);

  const ctaRow = (
    <>
      {current.mealIdentified && (
        <MealInsightActionButton
          label={<Translate msg="mealInsights.refine" />}
          onClick={() => setRefining(true)}
        />
      )}
      <MealInsightActionButton
        label={<Translate msg="mealInsights.delete" />}
        destructive
        onClick={() => setConfirmDeleteOpen(true)}
      />
    </>
  );

  const refineBar = (
    <MealRefineBar
      onSubmit={handleRefine}
      onCancel={() => setRefining(false)}
    />
  );

  const showRefineBar = refining && !isDesktop;

  return (
    <>
      <MealModal
        isOpen={isOpen}
        variant="center"
        style={MODAL_STYLE}
        onWillDismiss={handleClose}
        onDidDismiss={onClose}
      >
        <MealInsightContent
          imageUrl={imageUrl}
          imageLoaded={imageLoaded}
          loading={false}
          mealIdentified={current.mealIdentified}
          mealName={current.mealName}
          takenAt={takenAt}
          onEditTakenAt={() => setTakenAtDialogOpen(true)}
          caloriesRange={current.nutrients?.caloriesRange}
          insightsHtml={current.insights}
          nutrients={current.nutrients}
          showCloseButton
          closeContext="meals"
          onClose={handleClose}
          feedbackSlot={
            <Feedback
              initialRating={
                current.latestFeedback
                  ? FEEDBACK_TO_RATING[current.latestFeedback]
                  : null
              }
              onRate={(rating) => void rateMeal(meal.id, rating)}
            />
          }
          actionSlot={showRefineBar ? refineBar : ctaRow}
        />
      </MealModal>
      {refining && isDesktop && (
        <MealRefineDialog
          onSubmit={handleRefine}
          onCancel={() => setRefining(false)}
        />
      )}
      {takenAtDialogOpen && (
        <MealTakenAtEditor
          takenAt={takenAt}
          onClose={() => setTakenAtDialogOpen(false)}
          onSave={handleTakenAtSaved}
        />
      )}
      {confirmDeleteOpen && (
        <AppDialog
          confirmText="mealInsights.delete.confirm"
          confirmArrow={false}
          destructive
          hideClose
          cancelText="mealInsights.delete.no"
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => void handleConfirmDelete()}
        >
          <h5>
            <Translate msg="mealInsights.delete.confirmTitle" />
          </h5>
          <p>
            <Translate msg="mealInsights.delete.confirmBody" />
          </p>
        </AppDialog>
      )}
    </>
  );
};

export default MealInsightDialog;
